<template>
	<div class="form-textarea">
		<FormLabelTooltip
			v-if="label"
			:label="label"
			:name="`input-${name}`"
			:required="required"
			:tooltip="tooltip"
			:hidden="labelHidden"
		/>
		<textarea
			:id="`input-${name}`"
			class="b360-form-textarea"
			:class="{ 'b360-form-input--error': beyondMaxiumLength && !disabled }"
			:style="{ height: textAreaHeight }"
			:name="`input-${name}`"
			:value="value"
			:placeholder="placeholder"
			:maxlength="maxiumCharacterLength"
			:required="required"
			:disabled="disabled"
			@input="$emit('input', $event.target.value)"
			@keyup="autoGrowTextArea()"
			ref="textArea"
		>
		</textarea>
		<div
			v-if="showMaxLengthIndicator && maxLength"
			class="maxlength-indicator"
			:class="{ 'maxlength-indicator--error': beyondMaxiumLength && !disabled }"
		>
			{{ value.length }}/{{ maxLength
			}}<span class="visually-hidden"> characters.</span>
		</div>
	</div>
</template>

<script>
/**
 * About maxLength feature:
 * The way the component handles the maxLength prop is different when you set showMaxLengthIndicator value.
 * If showMaxLengthIndicator is false (or unset) the maxLength works just like it does on a regular html
 * textarea and limits how many characters can be entered.  If showMaxLengthIndicator is set to true the
 * user can enter more characters than the maxlength. Once they reach the limit, the textarea will get a
 * error border and the indicator to become the error color.
 */
import FormLabelTooltip from '@/components/global/FormLabelTooltip.vue';

export default {
	name: 'FormTextarea',
	props: {
		name: String,
		label: String,
		labelHidden: Boolean,
		placeholder: String,
		tooltip: String,
		value: String,
		required: Boolean,
		disabled: Boolean,
		maxLength: Number, //characters
		showMaxLengthIndicator: Boolean,
	},
	data() {
		return {
			valid: true,
			startingHeight: null,
			textAreaHeight: 'auto',
		};
	},
	computed: {
		maxiumCharacterLength() {
			return this.showMaxLengthIndicator ? null : this.maxLength;
		},
		beyondMaxiumLength() {
			if (
				!this.showMaxLengthIndicator ||
				!this.maxLength ||
				this.value.length <= this.maxLength
			) {
				return false;
			}
			return true;
		},
	},
	components: {
		FormLabelTooltip,
	},
	methods: {
		autoGrowTextArea() {
			this.textAreaHeight = 'auto';
			this.$nextTick(function () {
				const offset = 5;
				const textAreaScrollHeight = this.$refs.textArea.scrollHeight;
				const calculatedHeight =
					this.startingHeight > textAreaScrollHeight
						? this.startingHeight
						: textAreaScrollHeight + offset;
				this.textAreaHeight = `${calculatedHeight}px`;
			});
		},
	},
	watch: {
		value(val) {
			/*
			 * The handle-input event is an extra event and is not part of the two-way databinding.
			 * It was added here to make the API consistent with the FormInputs and FormRadioButtons components.
			 */
			this.$emit('handle-input', {
				value: this.value,
				fieldName: this.name,
			});
		},
	},
	mounted() {
		this.startingHeight = this.$refs.textArea.clientHeight;
		this.autoGrowTextArea();
	},
};
</script>

<style scoped lang="scss">
.form-textarea {
	display: flex;
	flex-direction: column;

	.b360-form-label {
		display: inline-flex;
	}

	.b360-label-holder {
		display: flex;
		justify-content: space-between;
	}

	textarea {
		resize: none;
		overflow-x: hidden;
		overflow-y: auto;
		min-height: 44px;
	}

	.maxlength-indicator {
		align-self: flex-end;
		@include rem(padding-right, 15px);
		color: $b360-color--gray__darker;
		@include typo--roboto__regular;
		font-size: $b360-font-size--small;
		line-height: 1.5;
		&.maxlength-indicator--error {
			color: $error;
		}
	}
}
</style>
